import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useQuery } from '@tanstack/react-query';
import { GridStack } from 'gridstack';
import { useContext, useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { LibraryManager, ResourceType } from 'libs.library_manager';
import { Badge, Button, H3, InlineNotification, Link as NucleusLink, Select, Spinner, useLocalizeMessage, useToastNotification, } from 'libs.nucleus';
import { PageHeader, PageWrapper, useBooleanState } from 'libs.react';
import { ApiClientService, AuthContext, useApiClient } from 'libs.react_internal';
import { DashboardChartCard } from './dashboard_chart_card.component';
import { DashboardEmptyState } from './dashboard_empty_state.component';
import { DashboardFilters } from './dashboard_filters.component';
import { StudiesContext } from '../../../contexts';
import { useChartsWithNewerVersion, useDashboards } from '../../hooks';
import { dashboardChartRefsAllHaveGridCoords, getStatusBadgeVariant, getStatusLabel, sortDashboardChartRefsForGrid, } from '../../utils';
import { ChangeStatusModal } from '../change_status_modal/change_status_modal.component';
import { EditDashboardModal } from '../edit_dashboard_modal/edit_dashboard_modal.component';
import { EditFiltersModal } from '../edit_filters_modal/edit_filters_modal.component';
import { ReviewVersionsModal } from '../review_versions_modal/review_versions_modal.component';
import 'gridstack/dist/gridstack.min.css';
// Grid configuration
const GRID_COLS = 12;
const GRID_ROW_HEIGHT = 120;
const GRID_MARGIN = 16;
export const DashboardDetail = () => {
    const { dashboardId } = useParams();
    const navigate = useNavigate();
    const translate = useLocalizeMessage();
    const { fetchDashboardById, updateDashboardData, cloneDashboard, archiveDashboard, isLoading: isApiLoading, } = useDashboards();
    const { entityId } = useContext(AuthContext);
    const { studies, isLoading: isLoadingStudies } = useContext(StudiesContext);
    const momClient = useApiClient(ApiClientService.MOM);
    const libraryClient = useApiClient(ApiClientService.LIBRARY);
    const { addNotification } = useToastNotification();
    const [dashboard, setDashboard] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isChangeStatusModalOpen, openChangeStatusModal, closeChangeStatusModal] = useBooleanState();
    const [isEditModalOpen, openEditModal, closeEditModal] = useBooleanState();
    const [isEditFiltersModalOpen, openEditFiltersModal, closeEditFiltersModal] = useBooleanState();
    const [isReviewVersionsModalOpen, openReviewVersionsModal, closeReviewVersionsModal] = useBooleanState();
    // Chart refs with layout (the main editable state)
    const [chartRefs, setChartRefs] = useState([]);
    // Study/Environment selection (runtime only, not persisted)
    const [selectedStudyId, setSelectedStudyId] = useState('');
    const [selectedEnvironmentId, setSelectedEnvironmentId] = useState('');
    const [runCount, setRunCount] = useState(0);
    const [filterParams, setFilterParams] = useState([]);
    const [dashboardCubeConfigIds, setDashboardCubeConfigIds] = useState([]);
    const chartCubeConfigCacheRef = useRef({});
    const libraryClientRef = useRef(libraryClient);
    libraryClientRef.current = libraryClient;
    /** Layout (drag/resize) is only allowed while the dashboard is In progress (DRAFT). */
    const isEditable = useMemo(() => dashboard?.status === 'DRAFT', [dashboard?.status]);
    // Newer-version tracking driven by chartRefs — the hook re-evaluates when
    // refs change after Apply, so the banner clears automatically without a
    // manual page refresh (SSP-3756 alignment with chart-builder behavior).
    const { chartsWithNewerVersions } = useChartsWithNewerVersion(chartRefs);
    // Gridstack refs
    const gridRef = useRef(null);
    const gridElRef = useRef(null);
    const gridKeyRef = useRef(0);
    const [gridKey, setGridKey] = useState(0);
    // ── Study options ─────────────────────────────────────────────────────────
    const studyOptions = useMemo(() => studies.map((study) => ({
        label: study.name,
        value: study.id.toString(),
    })), [studies]);
    const selectedStudy = useMemo(() => studyOptions.find((s) => s.value === selectedStudyId), [studyOptions, selectedStudyId]);
    // ── Environment fetching ──────────────────────────────────────────────────
    const fetchEnvironments = useCallback(async () => {
        if (!selectedStudyId) {
            return [];
        }
        try {
            const { data: response } = await momClient.get(`/v1/entities/${entityId}/studies/${selectedStudyId}/environments`, {
                params: {
                    clusterTypes: 'dev',
                    withCortexAccess: true,
                },
            });
            return response.data.filter((env) => !env.deployment.isProd);
        }
        catch {
            return [];
        }
    }, [selectedStudyId, entityId, momClient]);
    const { data: environments = [], isLoading: isLoadingEnvironments } = useQuery({
        queryKey: ['entities', entityId, 'studies', selectedStudyId, 'environments'],
        queryFn: fetchEnvironments,
        enabled: !!entityId && !!selectedStudyId,
        refetchOnWindowFocus: false,
    });
    const environmentOptions = useMemo(() => environments.map((env) => ({
        label: env.name,
        value: env.cortexId || env.id.toString(),
    })), [environments]);
    const selectedEnvironment = useMemo(() => environmentOptions.find((e) => e.value === selectedEnvironmentId), [environmentOptions, selectedEnvironmentId]);
    // Clear environment when study changes
    const handleStudyChange = useCallback((opt) => {
        setSelectedStudyId(opt.value);
        setSelectedEnvironmentId('');
    }, []);
    const handleEnvironmentChange = useCallback((opt) => {
        setSelectedEnvironmentId(opt.value);
    }, []);
    // Dirty tracking
    const [savedSnapshot, setSavedSnapshot] = useState('');
    const isDirty = useMemo(() => {
        if (!savedSnapshot) {
            return false;
        }
        return JSON.stringify(chartRefs) !== savedSnapshot;
    }, [chartRefs, savedSnapshot]);
    /** DOM / GridStack item order: match stored grid row/column when present. */
    const sortedChartRefs = useMemo(() => sortDashboardChartRefsForGrid(chartRefs), [chartRefs]);
    // Load dashboard
    useEffect(() => {
        if (!dashboardId) {
            navigate('/library/dashboards');
            return;
        }
        let isMounted = true;
        const loadDashboard = async () => {
            setIsLoading(true);
            try {
                const result = await fetchDashboardById(dashboardId);
                if (isMounted && result) {
                    setDashboard(result);
                    const charts = result.data?.charts || [];
                    setChartRefs(charts);
                    setSavedSnapshot(JSON.stringify(charts));
                    // Bump grid key to trigger gridstack initialization after data arrives
                    gridKeyRef.current += 1;
                    setGridKey(gridKeyRef.current);
                }
                else if (isMounted) {
                    navigate('/library/dashboards');
                }
            }
            catch {
                if (isMounted) {
                    navigate('/library/dashboards');
                }
            }
            finally {
                if (isMounted) {
                    setIsLoading(false);
                }
            }
        };
        loadDashboard();
        return () => {
            isMounted = false;
        };
    }, [dashboardId]);
    // GridStack x/y: use persisted gridX/gridY when all tiles have them; otherwise flow-pack by position (legacy).
    const computeLayout = useCallback((refs) => {
        if (dashboardChartRefsAllHaveGridCoords(refs)) {
            const map = new Map();
            for (const ref of refs) {
                map.set(ref.id, {
                    x: ref.gridX,
                    y: ref.gridY,
                    w: ref.xSpan || 12,
                    h: ref.ySpan || 2,
                });
            }
            return map;
        }
        const sorted = [...refs].sort((a, b) => a.position - b.position);
        const map = new Map();
        let currentX = 0;
        let currentY = 0;
        let maxRowH = 0;
        for (const ref of sorted) {
            const w = ref.xSpan || 12;
            const h = ref.ySpan || 2;
            if (currentX + w > GRID_COLS) {
                currentY += maxRowH;
                currentX = 0;
                maxRowH = 0;
            }
            map.set(ref.id, { x: currentX, y: currentY, w, h });
            currentX += w;
            maxRowH = Math.max(maxRowH, h);
        }
        return map;
    }, []);
    // Initialize GridStack after DOM renders. Intentionally not tied to chartRefs/sortedChartRefs
    // (that would reset the grid on every drag); only gridKey / isEditable re-run this effect.
    useEffect(() => {
        const el = gridElRef.current;
        if (!el || sortedChartRefs.length === 0) {
            return;
        }
        // Destroy previous instance if it exists
        if (gridRef.current) {
            gridRef.current.offAll();
            gridRef.current.removeAll(false);
            gridRef.current.destroy(false);
            gridRef.current = null;
        }
        const grid = GridStack.init({
            column: GRID_COLS,
            cellHeight: GRID_ROW_HEIGHT,
            margin: GRID_MARGIN,
            float: false,
            handle: '.drag-handle',
            animate: true,
            auto: false,
            resizable: { handles: 'e, s, se' },
            /** Ready (PUBLISHED) and other non-draft statuses: lock layout. */
            staticGrid: !isEditable,
        }, el);
        // Compute layout positions and register each item as a widget
        const layout = computeLayout(sortedChartRefs);
        const items = el.querySelectorAll('.grid-stack-item');
        items.forEach((itemEl) => {
            const id = itemEl.getAttribute('data-gs-id');
            if (!id) {
                return;
            }
            const pos = layout.get(id);
            if (pos) {
                grid.makeWidget(itemEl, {
                    id,
                    x: pos.x,
                    y: pos.y,
                    w: pos.w,
                    h: pos.h,
                    minW: 2,
                    minH: 1,
                });
            }
        });
        gridRef.current = grid;
        // Sync layout changes back to chartRefs state (static grid does not emit changes from user actions)
        if (isEditable) {
            grid.on('change', () => {
                const allItems = grid.getGridItems();
                const allNodes = allItems.map((item) => item.gridstackNode).filter((n) => !!n);
                const sorted = [...allNodes].sort((a, b) => (a.y ?? 0) !== (b.y ?? 0) ? (a.y ?? 0) - (b.y ?? 0) : (a.x ?? 0) - (b.x ?? 0));
                setChartRefs((prev) => prev.map((ref) => {
                    const posIndex = sorted.findIndex((n) => String(n.id) === String(ref.id));
                    const node = sorted[posIndex];
                    if (node) {
                        return {
                            ...ref,
                            xSpan: node.w,
                            ySpan: node.h,
                            position: posIndex + 1,
                            gridX: node.x ?? 0,
                            gridY: node.y ?? 0,
                        };
                    }
                    return ref;
                }));
            });
        }
        return () => {
            if (gridRef.current) {
                gridRef.current.offAll();
                gridRef.current.destroy(false);
                gridRef.current = null;
            }
        };
    }, [gridKey, isEditable]);
    // Status change handler
    const handleStatusChange = useCallback((updatedDashboard) => {
        if (updatedDashboard.id !== dashboardId) {
            navigate(`/library/dashboards/${updatedDashboard.id}`);
        }
        else {
            setDashboard(updatedDashboard);
        }
    }, [dashboardId, navigate]);
    // Edit save handler — re-fetches dashboard from server to ensure full data consistency
    const handleDetailsSave = useCallback(async () => {
        if (!dashboardId) {
            return;
        }
        const refreshed = await fetchDashboardById(dashboardId);
        if (refreshed) {
            setDashboard(refreshed);
            const charts = refreshed.data?.charts || [];
            setChartRefs(charts);
            setSavedSnapshot(JSON.stringify(charts));
        }
        gridKeyRef.current += 1;
        setGridKey(gridKeyRef.current);
    }, [dashboardId, fetchDashboardById]);
    // Run handler
    const handleRun = useCallback(() => {
        setRunCount((prev) => prev + 1);
    }, []);
    /** Merge live GridStack x/y into refs before persisting (covers save without a prior `change` event). */
    const resolveChartRefsFromLiveGrid = useCallback((refs) => {
        const grid = gridRef.current;
        if (!grid || !isEditable) {
            return refs;
        }
        const allNodes = grid
            .getGridItems()
            .map((item) => item.gridstackNode)
            .filter((n) => !!n);
        if (allNodes.length === 0) {
            return refs;
        }
        const sorted = [...allNodes].sort((a, b) => (a.y ?? 0) !== (b.y ?? 0) ? (a.y ?? 0) - (b.y ?? 0) : (a.x ?? 0) - (b.x ?? 0));
        return refs.map((ref) => {
            const posIndex = sorted.findIndex((n) => String(n.id) === String(ref.id));
            const node = sorted[posIndex];
            if (!node) {
                return ref;
            }
            return {
                ...ref,
                xSpan: node.w,
                ySpan: node.h,
                position: posIndex + 1,
                gridX: node.x ?? 0,
                gridY: node.y ?? 0,
            };
        });
    }, [isEditable]);
    // Save handler
    const handleSave = useCallback(async () => {
        if (!dashboard) {
            return;
        }
        const existingData = dashboard.data;
        const savedCubeConfigId = dashboard.cubeConfigId || existingData?.cubeConfigId || undefined;
        const savedCubeConfigKey = dashboard.cubeConfigKey || existingData?.cubeConfigKey || undefined;
        // Persist charts in visual (row-major) order so downstream consumers that don't apply
        // their own gridX/gridY sort (e.g., the study reports renderer) still see the right order.
        // See SSP-3849.
        const chartsToSave = sortDashboardChartRefsForGrid(resolveChartRefsFromLiveGrid(chartRefs));
        await updateDashboardData(dashboard.id, {
            name: dashboard.data?.name ?? dashboard.name,
            description: dashboard.data?.description ?? '',
            charts: chartsToSave,
            filters: dashboard.data?.filters,
            cubeConfigId: savedCubeConfigId,
            cubeConfigKey: savedCubeConfigKey,
        });
        setChartRefs(chartsToSave);
        setSavedSnapshot(JSON.stringify(chartsToSave));
        const updated = await fetchDashboardById(dashboard.id);
        if (updated) {
            setDashboard(updated);
        }
    }, [dashboard, chartRefs, resolveChartRefsFromLiveGrid, updateDashboardData, fetchDashboardById]);
    // Filters save handler
    const handleFiltersSave = useCallback(async (filters) => {
        if (!dashboard) {
            return;
        }
        const existingData = dashboard.data;
        const savedCubeConfigId = dashboard.cubeConfigId || existingData?.cubeConfigId || undefined;
        const savedCubeConfigKey = dashboard.cubeConfigKey || existingData?.cubeConfigKey || undefined;
        const chartsToSave = sortDashboardChartRefsForGrid(resolveChartRefsFromLiveGrid(chartRefs));
        await updateDashboardData(dashboard.id, {
            name: dashboard.data?.name ?? dashboard.name,
            description: dashboard.data?.description ?? '',
            charts: chartsToSave,
            filters,
            cubeConfigId: savedCubeConfigId,
            cubeConfigKey: savedCubeConfigKey,
        });
        setChartRefs(chartsToSave);
        setSavedSnapshot(JSON.stringify(chartsToSave));
        const updated = await fetchDashboardById(dashboard.id);
        if (updated) {
            setDashboard(updated);
        }
    }, [dashboard, chartRefs, resolveChartRefsFromLiveGrid, updateDashboardData, fetchDashboardById]);
    const handleReviewVersionsApply = useCallback(async (updatedRefs) => {
        if (!dashboard) {
            return;
        }
        const sortedRefs = sortDashboardChartRefsForGrid(updatedRefs);
        if (dashboard.status === 'PUBLISHED') {
            // SSP-4186: PUBLISHED-branch upgrade must be atomic — either the new
            // version becomes visible AND the old version is archived, or nothing
            // changes at all. Historically these two calls ran back-to-back with
            // no error handling, so a failed archive left both v(n) and v(n+1)
            // active in the Library and duplicate rows appeared in the list.
            const dataWithUpdatedCharts = {
                ...(dashboard.data || {}),
                charts: sortedRefs,
            };
            let newDashboard;
            try {
                newDashboard = await cloneDashboard({ ...dashboard, data: dataWithUpdatedCharts }, 'CREATE_VERSION', undefined, { silent: true });
            }
            catch {
                addNotification({
                    title: translate('Version upgrade failed'),
                    subtitle: translate('Could not create the new dashboard version. No changes were made.'),
                    type: 'error',
                });
                return;
            }
            try {
                await archiveDashboard(dashboard.id, { silent: true });
            }
            catch {
                // One retry — most archive failures are transient (network, rate limit).
                try {
                    await archiveDashboard(dashboard.id, { silent: true });
                }
                catch {
                    // Compensating archive: hide the newly-created version so the invariant
                    // "at most one active resource per key" is preserved. The user is told
                    // no changes were kept so they can retry the upgrade.
                    try {
                        await archiveDashboard(newDashboard.id, { silent: true });
                    }
                    catch {
                        // Both archives failed; the client-side dedup in useDashboards will
                        // still hide the duplication on the Library list, but flag this
                        // explicitly so the user can investigate.
                    }
                    addNotification({
                        title: translate('Version upgrade failed'),
                        subtitle: translate('Could not archive the previous version. No changes were kept.'),
                        type: 'error',
                    });
                    return;
                }
            }
            addNotification({
                title: translate('Dashboard updated to latest chart versions'),
                type: 'success',
            });
            navigate(`/library/dashboards/${newDashboard.id}`);
        }
        else {
            const existingData = dashboard.data;
            const savedCubeConfigId = dashboard.cubeConfigId || existingData?.cubeConfigId || undefined;
            const savedCubeConfigKey = dashboard.cubeConfigKey || existingData?.cubeConfigKey || undefined;
            await updateDashboardData(dashboard.id, {
                name: dashboard.data?.name ?? dashboard.name,
                description: dashboard.data?.description ?? '',
                charts: sortedRefs,
                filters: dashboard.data?.filters,
                cubeConfigId: savedCubeConfigId,
                cubeConfigKey: savedCubeConfigKey,
            });
            const updated = await fetchDashboardById(dashboard.id);
            if (updated) {
                setDashboard(updated);
                const charts = updated.data?.charts || [];
                setChartRefs(charts);
                setSavedSnapshot(JSON.stringify(charts));
                gridKeyRef.current += 1;
                setGridKey(gridKeyRef.current);
                // useChartsWithNewerVersion re-evaluates when chartRefs change, so
                // there's no need to manually clear a marker set here anymore.
            }
        }
    }, [
        dashboard,
        updateDashboardData,
        fetchDashboardById,
        cloneDashboard,
        archiveDashboard,
        navigate,
        addNotification,
        translate,
    ]);
    // ── Dataset ID (for filters modal) ───────────────────────────────────────
    const dashboardCubeConfigId = useMemo(() => {
        if (!dashboard) {
            return undefined;
        }
        const existingData = dashboard.data;
        return dashboard.cubeConfigId || existingData?.cubeConfigId || undefined;
    }, [dashboard]);
    const chartResourceIdsKey = useMemo(() => {
        const ids = chartRefs.map((ref) => ref.libraryResourceId || ref.id).filter(Boolean);
        return [...new Set(ids)].sort().join('|');
    }, [chartRefs]);
    useEffect(() => {
        let isMounted = true;
        const resolveCubeConfigIds = async () => {
            const ids = new Set();
            if (dashboardCubeConfigId) {
                ids.add(dashboardCubeConfigId);
            }
            const chartResourceIds = chartResourceIdsKey ? chartResourceIdsKey.split('|').filter(Boolean) : [];
            if (chartResourceIds.length > 0) {
                const libraryManager = new LibraryManager({ axiosInstance: libraryClientRef.current });
                const unresolvedIds = chartResourceIds.filter((resourceId) => !(resourceId in chartCubeConfigCacheRef.current));
                if (unresolvedIds.length > 0) {
                    const chartResources = await Promise.all(unresolvedIds.map((resourceId) => libraryManager.getResourceById(resourceId, ResourceType.REPORTING_CHART)));
                    unresolvedIds.forEach((resourceId, index) => {
                        const chart = chartResources[index];
                        const chartData = (chart?.data || {});
                        const cubeConfigId = chart?.cubeConfigId || chartData.cubeConfigId;
                        chartCubeConfigCacheRef.current[resourceId] = cubeConfigId;
                    });
                }
                chartResourceIds.forEach((resourceId) => {
                    const cubeConfigId = chartCubeConfigCacheRef.current[resourceId];
                    if (cubeConfigId) {
                        ids.add(cubeConfigId);
                    }
                });
            }
            if (isMounted) {
                setDashboardCubeConfigIds([...ids]);
            }
        };
        resolveCubeConfigIds().catch(() => {
            if (isMounted) {
                setDashboardCubeConfigIds(dashboardCubeConfigId ? [dashboardCubeConfigId] : []);
            }
        });
        return () => {
            isMounted = false;
        };
    }, [dashboardCubeConfigId, chartResourceIdsKey]);
    // ── Default filter initialization ────────────────────────────────────────
    const filtersWithDefaults = useMemo(() => (dashboard?.data?.filters || []).filter((f) => f.defaultOperator && f.defaultValues?.length), [dashboard?.data?.filters]);
    const defaultFilterParams = useMemo(() => filtersWithDefaults.map((f) => ({
        member: f.key,
        operator: f.defaultOperator,
        values: f.defaultValues,
    })), [filtersWithDefaults]);
    const hasSeededDefaultsRef = useRef(false);
    useEffect(() => {
        if (hasSeededDefaultsRef.current || !dashboard?.data?.filters) {
            return;
        }
        if (defaultFilterParams.length > 0) {
            setFilterParams(defaultFilterParams);
            hasSeededDefaultsRef.current = true;
        }
    }, [defaultFilterParams, dashboard?.data?.filters]);
    // ── Breadcrumbs ───────────────────────────────────────────────────────────
    const breadcrumbPages = useMemo(() => [
        { name: translate('Dashboards'), path: '/library/dashboards' },
        { name: dashboard?.name ?? '', path: '' },
    ], [dashboard?.name, translate]);
    // ── Header options ────────────────────────────────────────────────────────
    const headerOptions = useMemo(() => {
        if (!dashboard) {
            return [];
        }
        return [
            {
                name: translate('Change status'),
                onClick: openChangeStatusModal,
                variant: 'secondary',
                disabled: isApiLoading || isDirty,
            },
            {
                name: translate('Edit'),
                onClick: openEditModal,
                variant: 'secondary',
                disabled: isApiLoading || !isEditable,
            },
            {
                name: isDirty ? `${translate('Save')} *` : translate('Save'),
                onClick: handleSave,
                variant: 'primary',
                disabled: isApiLoading || !isDirty || !isEditable,
            },
        ];
    }, [dashboard, translate, openChangeStatusModal, openEditModal, handleSave, isApiLoading, isDirty, isEditable]);
    // ── Loading state ─────────────────────────────────────────────────────────
    if (isLoading || !dashboard) {
        return (_jsx(PageWrapper, { className: 'bg-neutral-100', children: _jsx(Spinner, { wrapper: 'full' }) }));
    }
    return (_jsxs(PageWrapper, { className: 'bg-neutral-100', children: [_jsx(PageHeader, { breadcrumbPages: breadcrumbPages, LinkComponent: Link, title: _jsxs("div", { className: 'flex items-center gap-x-2', children: [_jsx(H3, { children: dashboard.name }), _jsx(Badge, { variant: getStatusBadgeVariant(dashboard.status), label: getStatusLabel(dashboard.status, translate) }), _jsx(Badge, { variant: 'common', label: `v${dashboard.version}` })] }), subtitle: dashboard.data?.description, options: headerOptions }), chartsWithNewerVersions.size > 0 && (_jsx("div", { className: 'mb-4', children: _jsx(InlineNotification, { type: 'info', title: translate('New versions available'), subtitle: translate('Some of the charts in this dashboard have a newer version available in the Library.'), action: _jsx(NucleusLink, { href: '#', onClick: (e) => {
                            e.preventDefault();
                            openReviewVersionsModal();
                        }, children: translate('Review versions') }) }) })), _jsx("div", { className: 'flex items-center justify-between mb-4', children: _jsxs("div", { className: 'flex items-center gap-4', children: [_jsx("div", { className: 'w-40', children: _jsx(Select, { placeholder: translate('Select study'), options: studyOptions, value: selectedStudy, onChange: handleStudyChange, width: 'full', disabled: isLoadingStudies, searchableOptions: true }) }), _jsx("div", { className: 'w-48', children: _jsx(Select, { placeholder: translate('Select environment'), options: environmentOptions, value: selectedEnvironment, onChange: handleEnvironmentChange, width: 'full', disabled: !selectedStudyId || isLoadingEnvironments, searchableOptions: true }) }), _jsx(Button, { label: translate('Run'), variant: 'secondary', onClick: handleRun, disabled: !selectedStudyId || !selectedEnvironmentId }), dashboardCubeConfigId && (_jsx(Button, { label: translate('Edit filters'), variant: 'secondary', onClick: openEditFiltersModal, disabled: isApiLoading || !isEditable }))] }) }), dashboard.data?.filters && dashboard.data.filters.length > 0 && (_jsx(DashboardFilters, { filters: dashboard.data.filters, studyId: selectedStudyId, environmentId: selectedEnvironmentId, runCount: runCount, cubeConfigIds: dashboardCubeConfigIds, onApply: setFilterParams })), _jsxs("div", { className: 'flex-1 overflow-auto', children: [chartRefs.length === 0 && _jsx(DashboardEmptyState, {}), sortedChartRefs.length > 0 && (_jsx("div", { ref: gridElRef, className: 'grid-stack', children: sortedChartRefs.map((ref) => (_jsx("div", { className: 'grid-stack-item', "data-gs-id": ref.id, children: _jsx("div", { className: 'grid-stack-item-content', children: _jsx("div", { className: isEditable ? 'drag-handle cursor-grab active:cursor-grabbing size-full' : 'size-full', children: _jsx(DashboardChartCard, { chartId: ref.libraryResourceId || ref.id, chartName: ref.title || ref.name || translate('Untitled chart'), studyId: selectedStudyId, environmentId: selectedEnvironmentId, runCount: runCount, cubeConfigIds: dashboardCubeConfigIds, filterParams: filterParams }) }) }) }, ref.id))) }))] }), isChangeStatusModalOpen && (_jsx(ChangeStatusModal, { dashboard: dashboard, onClose: closeChangeStatusModal, onStatusChange: handleStatusChange })), _jsx(EditDashboardModal, { isOpen: isEditModalOpen, onClose: closeEditModal, onSave: handleDetailsSave, dashboard: dashboard, chartRefs: chartRefs }), _jsx(EditFiltersModal, { isOpen: isEditFiltersModalOpen, onClose: closeEditFiltersModal, onSave: handleFiltersSave, dashboardName: dashboard.name, cubeConfigId: dashboardCubeConfigId, existingFilters: dashboard.data?.filters || [], studyId: selectedStudyId, environmentId: selectedEnvironmentId, cubeConfigIds: dashboardCubeConfigIds, studyOptions: studyOptions, environmentOptions: environmentOptions, selectedStudy: selectedStudy, selectedEnvironment: selectedEnvironment, onStudyChange: handleStudyChange, onEnvironmentChange: handleEnvironmentChange, isLoadingStudies: isLoadingStudies, isLoadingEnvironments: isLoadingEnvironments }), _jsx(ReviewVersionsModal, { isOpen: isReviewVersionsModalOpen, onClose: closeReviewVersionsModal, chartRefs: chartRefs, chartsWithNewerVersions: chartsWithNewerVersions, dashboardStatus: dashboard.status, onApply: handleReviewVersionsApply })] }));
};
